import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
export { AuthContext };

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Check URL params BEFORE component renders (impersonation flow)
const checkImpersonation = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const impersonateToken = urlParams.get('impersonate');
  if (impersonateToken) {
    sessionStorage.setItem('isImpersonating', 'true');
    sessionStorage.setItem('_at', impersonateToken);
    // Decode the JWT payload (no verification) to pick up auth_type so initAuth
    // uses the v2 endpoint that returns subscription_status, ensuring suspended
    // tenants are properly gated even under impersonation.
    try {
      const payload = JSON.parse(atob(impersonateToken.split('.')[1]));
      const authType = payload.auth_type === 'unified' ? 'unified' : 'v2';
      sessionStorage.setItem('authType', authType);
    } catch {
      sessionStorage.setItem('authType', 'v2');
    }
    window.history.replaceState({}, document.title, window.location.pathname);
    return impersonateToken;
  }
  return sessionStorage.getItem('_at') || null;
};

const getStoredAuthType = () => sessionStorage.getItem('authType') || 'legacy';

const getStoredUser = () => {
  try {
    const stored = sessionStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredUser);
  const tokenRef = useRef(checkImpersonation());
  const [token, setTokenState] = useState(tokenRef.current);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(
    () => sessionStorage.getItem('isImpersonating') === 'true'
  );
  
  const skipNextInitRef = useRef(false);
  
  const [authType, setAuthType] = useState(getStoredAuthType);
  const [tenants, setTenants] = useState([]);
  const [activeTenant, setActiveTenant] = useState(null);
  const [pendingTenantSelection, setPendingTenantSelection] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [gracePeriodExpiresAt, setGracePeriodExpiresAt] = useState(null);
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);

  // Set token in both ref and state (ref for immediate access in interceptors)
  const setToken = useCallback((newToken) => {
    tokenRef.current = newToken;
    setTokenState(newToken);
    if (newToken) {
      sessionStorage.setItem('_at', newToken);
    } else {
      sessionStorage.removeItem('_at');
    }
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(userData);
    if (userData) {
      sessionStorage.setItem('user', JSON.stringify(userData));
    } else {
      sessionStorage.removeItem('user');
    }
  }, []);

  const logout = useCallback(async () => {
    const currentToken = tokenRef.current;
    if (currentToken) {
      try {
        await axios.post(`${API_URL}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${currentToken}` },
          withCredentials: true
        });
      } catch { /* proceed with local cleanup */ }
    }
    sessionStorage.clear();
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
    setIsImpersonating(false);
    setAuthType('legacy');
    setTenants([]);
    setActiveTenant(null);
    setPendingTenantSelection(false);
    setSubscriptionStatus(null);
    setGracePeriodExpiresAt(null);
    setTrialEndsAt(null);
  }, [setToken]);

  const clearMustChangePassword = useCallback(() => {
    setMustChangePassword(false);
    setUser(prev => {
      const updated = prev ? { ...prev, must_change_password: false } : null;
      if (updated) sessionStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const refreshUser = useCallback(async () => {
    const currentToken = tokenRef.current;
    if (!currentToken) return;
    try {
      const isV2Like = authType === 'v2' || authType === 'unified';
      const endpoint = isV2Like ? '/v2/auth/my-tenants' : '/auth/me';
      const response = await axios.get(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${currentToken}` },
        withCredentials: true
      });
      
      if (isV2Like) {
        setTenants(response.data.tenants || []);
        setActiveTenant(response.data.active_tenant);
        setSubscriptionStatus(response.data.subscription_status || null);
        setGracePeriodExpiresAt(response.data.grace_period_expires_at || null);
        setTrialEndsAt(response.data.trial_ends_at || null);
        setPaymentProcessing(!!response.data.payment_processing);
        if (response.data.user) {
          const u = { ...response.data.user };
          const at = response.data.active_tenant;
          if (at) {
            if (u.industry == null) u.industry = at.industry;
            if (u.company_name == null) u.company_name = at.company_name;
          }
          updateUser(u);
        }
      } else {
        updateUser(response.data);
        setMustChangePassword(response.data.must_change_password || false);
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, [authType, updateUser]);

  useEffect(() => {
    const initAuth = async () => {
      if (skipNextInitRef.current) {
        skipNextInitRef.current = false;
        setLoading(false);
        return;
      }
      
      const currentToken = tokenRef.current;
      if (!currentToken) {
        setLoading(false);
        return;
      }
      
      try {
        if (authType === 'v2' || authType === 'unified') {
          const response = await axios.get(`${API_URL}/v2/auth/my-tenants`, {
            headers: { Authorization: `Bearer ${currentToken}` },
            withCredentials: true
          });
          setTenants(response.data.tenants || []);
          setActiveTenant(response.data.active_tenant);
          setSubscriptionStatus(response.data.subscription_status || null);
          setGracePeriodExpiresAt(response.data.grace_period_expires_at || null);
          setTrialEndsAt(response.data.trial_ends_at || null);
          setPaymentProcessing(!!response.data.payment_processing);
          if (response.data.user) {
            const u = { ...response.data.user };
            // Backfill industry/company_name from the active tenant when the
            // user object omits them, so industry-gated UI survives reloads.
            const at = response.data.active_tenant;
            if (at) {
              if (u.industry == null) u.industry = at.industry;
              if (u.company_name == null) u.company_name = at.company_name;
            }
            updateUser(u);
            setMustChangePassword(response.data.user.must_change_password || false);
          } else if (response.data.active_tenant) {
            updateUser({
              id: response.data.active_tenant.user_id,
              email: response.data.active_tenant.email,
              full_name: response.data.active_tenant.full_name,
              role: response.data.active_tenant.role,
              roles: response.data.active_tenant.roles || [response.data.active_tenant.role],
              client_id: response.data.active_tenant.client_id,
              industry: response.data.active_tenant.industry,
            });
          }
        } else {
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${currentToken}` },
            withCredentials: true
          });
          updateUser(response.data);
          setSubscriptionStatus(response.data.subscription_status || null);
          setMustChangePassword(response.data.must_change_password || false);
        }
      } catch {
        if (authType === 'v2' || authType === 'unified') {
          try {
            const response = await axios.get(`${API_URL}/auth/me`, {
              headers: { Authorization: `Bearer ${currentToken}` },
              withCredentials: true
            });
            updateUser(response.data);
            setSubscriptionStatus(response.data.subscription_status || null);
            setAuthType('legacy');
            sessionStorage.setItem('authType', 'legacy');
          } catch {
            setToken(null);
            setUser(null);
            sessionStorage.removeItem('user');
          }
        } else {
          setToken(null);
          setUser(null);
          sessionStorage.removeItem('user');
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [authType, logout, updateUser, setToken]);

  // Legacy login
  const login = async (clientId, username, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, {
      client_id: clientId, username, password
    }, { withCredentials: true });
    const { access_token, user: userData } = response.data;
    
    sessionStorage.setItem('authType', 'legacy');
    sessionStorage.removeItem('isImpersonating');
    
    skipNextInitRef.current = true;
    setLoading(false);
    
    setToken(access_token);
    updateUser(userData);
    setAuthType('legacy');
    setMustChangePassword(userData.must_change_password || false);
    setIsImpersonating(false);
    setSubscriptionStatus(userData.subscription_status || null);
    setGracePeriodExpiresAt(userData.grace_period_expires_at || null);
    setPaymentProcessing(!!userData.payment_processing);
    return userData;
  };

  // V2 Login
  const loginV2 = async (email, password) => {
    const response = await axios.post(`${API_URL}/v2/auth/login`, {
      email, password
    }, { withCredentials: true });
    const { access_token, user: userData, tenants: userTenants, requires_tenant_selection, subscription_status, grace_period_expires_at, trial_ends_at, payment_processing } = response.data;
    
    sessionStorage.setItem('authType', 'v2');
    sessionStorage.removeItem('isImpersonating');
    
    if (!userData.role && !requires_tenant_selection && userTenants?.length > 0) {
      const activeTenantInfo = userTenants.find(t => t.client_id === userData.active_tenant);
      if (activeTenantInfo) {
        userData.role = activeTenantInfo.role;
        userData.roles = activeTenantInfo.roles || [activeTenantInfo.role];
        userData.client_id = activeTenantInfo.client_id;
        userData.industry = activeTenantInfo.industry;
        userData.company_name = activeTenantInfo.company_name;
        if (activeTenantInfo.logo_url) userData.logo_url = activeTenantInfo.logo_url;
      }
    }
    // Always backfill tenant-derived fields (industry, company_name) from the
    // active tenant when the root user object omits them. This is needed for
    // admins whose login response already carries `role` (the block above is
    // skipped for them), otherwise industry-gated UI (e.g. concrete pumping
    // docket fields) never activates.
    if (!requires_tenant_selection && userTenants?.length > 0) {
      const activeTenantInfo = userTenants.find(t => t.client_id === (userData.active_tenant || userData.client_id));
      if (activeTenantInfo) {
        if (userData.industry == null) userData.industry = activeTenantInfo.industry;
        if (userData.company_name == null) userData.company_name = activeTenantInfo.company_name;
      }
    }
    if (!userData.roles && userData.role) userData.roles = [userData.role];
    
    skipNextInitRef.current = true;
    setLoading(false);
    
    setToken(access_token);
    updateUser(userData);
    setAuthType('v2');
    setTenants(userTenants || []);
    setActiveTenant(userData.active_tenant);
    setMustChangePassword(userData.must_change_password || false);
    setIsImpersonating(false);
    setPendingTenantSelection(requires_tenant_selection);
    setSubscriptionStatus(subscription_status || userData?.subscription_status || null);
    setGracePeriodExpiresAt(grace_period_expires_at || userData?.grace_period_expires_at || null);
    setTrialEndsAt(trial_ends_at || userData?.trial_ends_at || null);
    setPaymentProcessing(!!payment_processing);
    
    return { user: userData, tenants: userTenants, requiresTenantSelection: requires_tenant_selection };
  };

  // V2 Select Tenant
  const selectTenant = async (clientId) => {
    setIsRefreshingToken(true);
    try {
      const currentToken = tokenRef.current;
      const response = await axios.post(`${API_URL}/v2/auth/select-tenant`, 
        { client_id: clientId },
        { headers: { Authorization: `Bearer ${currentToken}` }, withCredentials: true }
      );
      const { access_token, user: userData, tenants: userTenants, subscription_status, grace_period_expires_at, trial_ends_at, payment_processing: pp } = response.data;
      
      // Backfill industry/company_name so industry-gated UI (e.g. concrete
      // pumping docket fields) activates after switching tenants. The
      // select-tenant response carries `tenant_industry`, not `industry`.
      if (userData) {
        if (userData.industry == null) {
          userData.industry =
            userData.tenant_industry ??
            (userTenants || []).find(t => t.client_id === userData.active_tenant)?.industry ??
            null;
        }
      }
      
      skipNextInitRef.current = true;
      setLoading(false);
      
      setToken(access_token);
      updateUser(userData);
      setTenants(userTenants || []);
      setActiveTenant(userData.active_tenant);
      setPendingTenantSelection(false);
      setSubscriptionStatus(subscription_status || null);
      setGracePeriodExpiresAt(grace_period_expires_at || null);
      setTrialEndsAt(trial_ends_at || null);
      setPaymentProcessing(!!pp);
      
      return userData;
    } finally {
      setTimeout(() => setIsRefreshingToken(false), 500);
    }
  };

  const register = async (clientId, username, password, fullName, email, role) => {
    const response = await axios.post(`${API_URL}/auth/register`, {
      client_id: clientId, username, password, full_name: fullName, email, role
    }, { withCredentials: true });
    const { access_token, user: userData } = response.data;
    sessionStorage.setItem('authType', 'legacy');
    setToken(access_token);
    updateUser(userData);
    setAuthType('legacy');
    return userData;
  };

  // API instance — uses Bearer header from memory (not localStorage)
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL,
      withCredentials: true
    });

    instance.interceptors.request.use(
      config => {
        // Always read from ref for latest token (synchronous, no stale closure)
        const currentToken = tokenRef.current;
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
        return config;
      },
      error => Promise.reject(error)
    );

    instance.interceptors.response.use(
      response => response,
      async error => {
        if (error.response?.status === 401 && !isRefreshingToken && tokenRef.current) {
          try {
            const checkResponse = await axios.post(`${API_URL}/auth/check-session-termination`, {}, {
              headers: { Authorization: `Bearer ${tokenRef.current}` }
            });
            const { terminated_reason, message } = checkResponse.data;
            
            if (terminated_reason === 'new_login') {
              const { toast } = await import('sonner');
              toast.info(message || 'Your session was ended because you logged in from another device.', { duration: 6000 });
            }
          } catch { /* ignore */ }
          
          logout();
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, [logout, isRefreshingToken]);

  return (
    <AuthContext.Provider value={{ 
      user, token, loading, login, loginV2, selectTenant, register, logout, api, setToken,
      isImpersonating, mustChangePassword, clearMustChangePassword, refreshUser,
      authType, tenants, activeTenant, pendingTenantSelection,
      subscriptionStatus, gracePeriodExpiresAt, trialEndsAt, paymentProcessing
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
